import { PageMasterMode, usePageContext } from "../../../../components/navigation/PageContext";

import {
  useMeetingSelector
} from "@kalyzee/kast-web-sdk";
import {
  audioContextIsReadyToUse,
  useRefAutoUpdate
} from "@kalyzee/kast-web-sdk/web-components";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toastError } from "../../../../helpers/toast";
import { buildMediasFromSources, MeetingSourceExtended, useMeetingConfigurator, useMeetingContext, useMeetingSources } from "../../../../hooks/meeting";

import { RTCStreamSenderParamsPreset } from "@kalyzee/kast-web-sdk/webrtc";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { DEFAULT_MAX_BITRATES } from "../../../../helpers/videos";
import { isMongoDBId } from "../../../../helpers/utils";

import { MeetingContainer } from "@kalyzee/kast-web-sdk/contexts/meeting/core/container";
import { SessionMode } from "../../../../contexts/appStore/hooks";
import { useAppStoreSelector } from "../../../../contexts/appStore/selectors";
import { useAppStoreDispatch } from "../../../../contexts/appStore/context";

import { LoadingView } from "../../../../components/utils/LoadingView";
import { MeetingContainerProvider } from "../../../../contexts/meetingContainer/provider";
import { MeetingUtilsProvider } from "../../../../contexts/meetingUtils/provider";
import { useDeviceControlsContext } from "../../../../contexts/deviceControls/context";
import { useSettings } from "../../../../hooks/settings";
import { useDelayedState } from "../../../../hooks/states";
import { ConnectedMeetingPage } from "./connected/base";

type Phase = "loading" | "joining" | "connected";

export const MeetingInitPage = () => {
  const pageContext = usePageContext<SessionMode.MASTER>();
  const pageContextRef = useRefAutoUpdate(pageContext);

  const audioEnabled = useAppStoreSelector(state => state.audioStreamEnabled);
  const videoEnabled = useAppStoreSelector(state => state.videoStreamEnabled);
  const sourcePreferences = useAppStoreSelector(state => state.sourcePreferences);

  const sources = useMeetingSources();

  const dispatch = useAppStoreDispatch();

  const deviceControlsCtx = useDeviceControlsContext();

  const meetingCtx = useMeetingContext();

  const [settings] = useSettings();
  const settingsRef = useRefAutoUpdate(settings);

  const meetingsAPI = meetingCtx?.api;
  const meetingsAPIRef = useRefAutoUpdate(meetingsAPI);

  const [phase, setPhase] = useState<Phase>("loading");

  const container = useMemo(() => {
    if (!meetingsAPI) return undefined;

    const containers = meetingsAPI.getContainers();
    const selectedId = pageContextRef.current?.params?.id;

    if (selectedId) {
      return containers.find((c) => c.getMeetingId() === selectedId);
    }

    return containers[0];
  }, [meetingsAPI]);

  // Avoids flickering
  const [showMeeting] = useDelayedState(container !== undefined, 1000);

  const joinContainer = useCallback(async (container: MeetingContainer) => {
    if (container.getStatus() !== "disconnected") return;

    const containerAPI = container.getAPI();

    const medias = buildMediasFromSources(sources as MeetingSourceExtended[], {
      getSourceEnabled: (sourceName) => {
        const pref = sourcePreferences[sourceName];
        return {
          audio: pref?.audioEnabled ?? audioEnabled,
          video: pref?.videoEnabled ?? videoEnabled,
        };
      },
    });

    const result = await containerAPI.join({
      medias,
    });

    const id = pageContextRef.current?.params?.id;

    if (result.error) {
      console.error(`[ERROR] join: `, result.error);
      toastError(`Une erreur est apparue.`);
      if (id) pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true });
      else pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      container.destroy();
      return;
    }
    const session = result.response?.session;
    if (!session) {
      console.error(`[ERROR] join - internal error: session is undefined`);
      toastError(`Une erreur est apparue.`);
      if (id) pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true });
      else pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      container.destroy();
      return;
    }

    deviceControlsCtx.applyEntryPresets();
    setPhase("connected");
  }, [audioEnabled, videoEnabled, sourcePreferences, sources, dispatch, deviceControlsCtx]);

  const joinTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const createAndJoinContainer = useCallback(async (id: string) => {
    const api = meetingsAPIRef.current;

    if (!api) {
      console.error(`[ERROR] join - meetingContext is undefined `);
      toastError(`Une erreur est apparue.`);
      if (id) pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true });
      else pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
      return;
    }

    joinTimeoutRef.current = setTimeout(() => {
      const container = api.getOrCreateContainer(id);
      joinContainer(container);
    }, 0);
  }, [joinContainer]);

  const leave = async () => {
    await deviceControlsCtx.applyExitPresets();
    const promises: Promise<any>[] = [];
    meetingsAPIRef.current?.getContainers()?.forEach((c) => {
      const result = c.getAPI()?.leave();
      if (!("error" in result)) promises.push(result);
    });
    await Promise.allSettled(promises);
  };

  useMeetingConfigurator(container?.getAPI(), {
    ovenMediaSession: {
      emission: {
        options: (mediaSession) => {
          if (mediaSession.name === "screen") {
            const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.screensharing ?? DEFAULT_MAX_BITRATES.student.screensharing;
            return {
              rtc: {
                streamParams: {
                  preset: RTCStreamSenderParamsPreset.VISIO_SCREENSHARING,
                  video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                  audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
                },
              },
            };
          }
          const maxBitrate = settingsRef.current?.debug?.maxBitrate?.student?.webcam ?? DEFAULT_MAX_BITRATES.student.webcam;
          return {
            rtc: {
              streamParams: {
                preset: RTCStreamSenderParamsPreset.VISIO_FACE,
                video: { rtcEncoding: { maxBitrate: maxBitrate?.video, networkPriority: "low" } },
                audio: { rtcEncoding: { maxBitrate: maxBitrate?.audio, networkPriority: "low" } },
              },
            },
          };
        },
      },
      reception: {
        options: () => ({
          video: { mode: "subscription", delayToDisableStream: 5000 },
          audio: true,
        }),
      },
    },
  });

  // Phase 1: test audio context
  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const id = pageContextRef.current?.params?.id;
      // Provider-scoped URL: id isn't a real meetingId yet — lobby resolves it.
      const hasProvider = !!pageContextRef.current?.searchParams.get("provider");
      // Short-id URL (anything that isn't a 24-char hex mongoId): lobby resolves it.
      const isShortId = !!id && !isMongoDBId(id);
      if (hasProvider || isShortId) {
        if (id) pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true });
        else pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
        return;
      }

      // Test audio context — if not ready (direct load without gesture), redirect to lobby
      const ready = await audioContextIsReadyToUse(getAudioContextForVolumeAnalyzer());
      if (cancelled) return;

      if (!ready) {
        if (id) pageContextRef.current?.goTo(PageMasterMode.LOBBY, { id }, { replace: true });
        else pageContextRef.current?.goTo(PageMasterMode.LIST, undefined, { queries: { meeting: undefined } });
        return;
      }

      setPhase("joining");
    };

    init();

    const onBeforeUnload = () => {
      leave();
    };
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      cancelled = true;
      window.removeEventListener("beforeunload", onBeforeUnload);
      if (joinTimeoutRef.current) clearTimeout(joinTimeoutRef.current);
      leave();
    };
  }, []);

  // Phase: joining → create container and join
  useEffect(() => {
    if (phase !== "joining") return;
    const id = pageContextRef.current?.params?.id ?? "";
    createAndJoinContainer(id);
  }, [phase]);

  if (phase === "loading") {
    return <LoadingView fullscreen message="Chargement du meeting ..." />;
  }

  if (phase === "joining" || !container || !showMeeting) {
    return <LoadingView fullscreen message="Connexion au meeting ..." />;
  }

  return (
    <MeetingContainerProvider container={container}>
      <MeetingUtilsProvider>
        <ConnectedMeetingPage />
      </MeetingUtilsProvider>
    </MeetingContainerProvider>
  );
};
